import React from 'react';

import { Wrapper } from './Block.jsx';
import { EmptyPlaceholder } from '../utility/blocks';
import Comment from './Comment.jsx';
import { InventoryCellContainer } from './InventoryCell.jsx';
import Name from '../utility/Name.jsx';
import TypeConfig from '../utility/TypeConfig.jsx';
import Utilities from '../utility/Utilities.jsx';

const defaultColumnCount = 3;
function InventoryListComponent(props) {
    let cellPadding, columnCount, inventory;
    if (props.isTransactional) {
        cellPadding = 0;
        columnCount = 1;
        inventory = props.data && props.data.Inventory || [];
    } else {
        cellPadding = InventoryCellContainer.TypeConfig.defaultCellPadding;
        columnCount = defaultColumnCount;
        inventory = props.blockConfig.inventory || [];
    }
    const isEmpty = inventory.length == 0;
    if (isEmpty) {
        if (props.builder) {
            return <EmptyPlaceholder fullWidth={true}>{props.isTransactional ? 'You must have at least one inventory resource to see a preview' : 'Select at least one inventory item'}</EmptyPlaceholder>
        }
        return null;
    }
    let rowSpacing = '18px';
    let cellConfig;
    const wrapperStyle = {};
    if (props.blockConfig) {
        cellConfig = props.blockConfig.block && props.blockConfig.block.cell;
        Utilities.mapStyleConfigProps(wrapperStyle, props.blockConfig.layout, (propName, propValue, propUnit) => {
            switch (propName) {
                case 'cellPadding':
                    cellPadding = parseInt(propValue, 10);
                    return true;
                case 'columnCount':
                    columnCount = parseInt(propValue, 10) || columnCount;
                    return true;
                case 'rowSpacing':
                    if (propValue && propUnit) {
                        rowSpacing = `${propValue}${propUnit}`;
                    }
                    return true;
            }
        });
    }
    const alignment = columnCount == 1 ? 'center' : 'left';
    const columnWidth = Utilities.columnWidth(columnCount);

    let cells = [];
    const rows = [];
    inventory.forEach((d, i) => {
        if (!d) {
            return;
        }
        const data = props.isTransactional ? d : d.data;
        if (!data) {
            return;
        }
        if (cells.length >= columnCount) {
            rows.push(cells);
            cells = [];
        }
        let contentConfig = cellConfig;
        if (d.useDefaultCellDesign != true && d.cell && d.cell.blocks && d.cell.blocks.length > 0) {
            contentConfig = d.cell;
        }
        cells.push(
            <InventoryCellContainer.Component
                key={data.EditionId || i}
                alignment={alignment}
                blockId={props.blockId}
                builder={props.builder}
                cellPadding={cellPadding}
                columnCount={columnCount}
                contentConfig={contentConfig}
                data={data}
                frameDoc={props.frameDoc}
                index={cells.length}
                renderMso={props.renderMso}
                serverSide={props.serverSide}
                width={columnWidth} />
        );
    });
    if (cells.length > 0) {
        rows.push(cells);
    }
    const content = rows.map((d, i) => {
        const rowStyle = {};
        if (i < rows.length - 1) {
            rowStyle.paddingBottom = rowSpacing;
        }
        return (
            <InventoryRow
                key={`row-${i}`}
                blockId={props.blockId}
                frameDoc={props.frameDoc}
                index={i}
                renderMso={props.renderMso}
                style={rowStyle}>
                {d}
            </InventoryRow>
        )
    });
    return <Wrapper {...props} isLayout={true} style={wrapperStyle}>{content}</Wrapper>
}
const CampaignInventoryListTypeConfig = {
    panel: {
        block: function () { },
        inventory: function (isTemplate = false) {
            const group = 'inventory';
            const config = {
                dynamic: true,
                dynamicContent: {
                    display: 'Inventory',
                    displayProperty: 'data',
                    fullWidth: true,
                    group,
                    type: 'sortable',
                    options: [
                        {
                            group,
                            name: 'inventory',
                            type: 'inventory'
                        }
                    ],
                    onHeaderDisplay: (data, config) => {
                        let imageUrl;
                        if (data && data.Image) {
                            imageUrl = Utilities.imageUrl({ extension: data.Image.Extension, id: data.Image.S3Id }, 20, 'c_fit');
                        }
                        let display = data && data.Title || '';
                        if (!config.useDefaultCellDesign && config.cell && config.cell.blocks && config.cell.blocks.length > 0) {
                            display = <>{display}<span className="indicator" /></>
                        }
                        return (
                            <span className="inventory-header">
                                <span className="inventory-header-image">
                                    <img src={imageUrl} />
                                </span>
                                <span className="inventory-header-text">{display}</span>
                            </span>
                        );
                    }
                },
                group,
                label: 'Inventory',
                options: [],
            };
            if (!isTemplate) {
                config.dynamicContent.options.push(
                    {
                        display: 'Use Default Cell Design',
                        fullWidth: true,
                        group: group,
                        name: 'useDefaultCellDesign',
                        type: 'checkbox',
                        value: true
                    },
                    {
                        display: 'Edit Cell For This Item',
                        group: group,
                        type: 'edit-text',
                        value: {
                            keyPath: ['cell'],
                            name: Name.Content.InventoryCellOverride
                        },
                        visible: (config) => config && config.useDefaultCellDesign == false
                    }
                )
            }
            config.options.push(
                {
                    display: 'Edit Inventory',
                    fullWidth: true,
                    group,
                    offsetTop: true,
                    type: 'edit-button',
                    value: config.dynamicContent
                },
                {
                    display: 'Edit Cell Design',
                    fullWidth: true,
                    group,
                    type: 'edit-button',
                    value: {
                        keyPath: ['block', 'cell'],
                        name: Name.Content.InventoryCell
                    }
                }
            );
            if (!isTemplate) {
                config.options.push({
                    fullWidth: true,
                    group,
                    type: 'info',
                    value: '* Inventory Items with a red dot indicate the default cell design has been overriden for that item'
                })
            } else {
                config.options.push({
                    fullWidth: true,
                    group,
                    type: 'spacer-short'
                })
            }
            return config;
        },
        layout: function () {
            const group = 'layout';
            return {
                label: 'Design',
                group,
                options: [
                    {
                        display: 'Background Color',
                        group,
                        name: 'backgroundColor',
                        type: 'color'
                    },
                    {
                        display: 'Column Count',
                        group,
                        name: 'columnCount',
                        options: [
                            { display: '1', value: 1 },
                            { display: '2', value: 2 },
                            { display: '3', value: 3 }
                        ],
                        type: 'dropdown',
                        value: defaultColumnCount
                    },
                    {
                        display: 'Cell Padding',
                        fullWidth: true,
                        group,
                        max: '100',
                        name: 'cellPadding',
                        options: TypeConfig.options.measurement(),
                        type: 'measurement-slider',
                        value: { value: `${InventoryCellContainer.TypeConfig.defaultCellPadding}`, unit: 'px' }
                    },
                    {
                        display: 'Row Spacing',
                        fullWidth: true,
                        group,
                        max: '100',
                        name: 'rowSpacing',
                        options: TypeConfig.options.measurement(),
                        type: 'measurement-slider',
                        value: { value: '18', unit: 'px' }
                    },
                    {
                        display: 'Padding Top',
                        fullWidth: true,
                        group,
                        max: '100',
                        name: 'paddingTop',
                        options: TypeConfig.options.measurement(),
                        type: 'measurement-slider'
                    },
                    {
                        display: 'Padding Bottom',
                        fullWidth: true,
                        group,
                        max: '100',
                        name: 'paddingBottom',
                        options: TypeConfig.options.measurement(),
                        type: 'measurement-slider'
                    }
                ]
            };
        }
    }
}
const CampaignInventoryListBlockTypeDefinition = (group, campaignType) => {
    return {
        id: Name.Block.Inventory.List,
        config: {
            block: {},
            inventory: CampaignInventoryListTypeConfig.panel.inventory(campaignType),
            layout: CampaignInventoryListTypeConfig.panel.layout(),
            defaults: [
                { configName: 'block', propertyName: 'cell', propertyValue: InventoryCellContainer.TypeConfig.defaultDesign({}) }
            ]
        },
        display: 'Inventory Container',
        group,
        image: 'inventory.png'
    };
}
const Campaign = { BlockTypeDefinition: CampaignInventoryListBlockTypeDefinition, Component: InventoryListComponent, TypeConfig: CampaignInventoryListTypeConfig };

function TransactionalInventoryListComponent(props) {
    return <InventoryListComponent {...props} isTransactional={true} />
}
const TransactionalInventoryListTypeConfig = {
    panel: {
        layout: function () {
            const group = 'layout';
            return {
                label: 'Design',
                group,
                options: [
                    {
                        display: 'Edit Cell Design',
                        fullWidth: true,
                        group,
                        type: 'edit-button',
                        value: {
                            keyPath: ['block', 'cell'],
                            name: Name.Content.Transactional.InventoryCell
                        }
                    },
                    {
                        fullWidth: true,
                        group,
                        type: 'spacer-short'
                    },
                    {
                        display: 'Background Color',
                        group,
                        name: 'backgroundColor',
                        type: 'color'
                    },
                    // {
                    //     display: 'Column Count',
                    //     group,
                    //     name: 'columnCount',
                    //     options: [
                    //         { display: '1', value: 1 },
                    //         { display: '2', value: 2 },
                    //         { display: '3', value: 3 }
                    //     ],
                    //     type: 'dropdown',
                    //     value: defaultColumnCount
                    // },
                    // {
                    //     display: 'Cell Padding',
                    //     fullWidth: true,
                    //     group,
                    //     max: '100',
                    //     name: 'cellPadding',
                    //     options: TypeConfig.options.measurement(),
                    //     type: 'measurement-slider',
                    //     value: { value: `${InventoryCellContainer.TypeConfig.defaultCellPadding}`, unit: 'px' }
                    // },
                    {
                        display: 'Row Spacing',
                        fullWidth: true,
                        group,
                        max: '100',
                        name: 'rowSpacing',
                        options: TypeConfig.options.measurement(),
                        type: 'measurement-slider',
                        value: { value: '18', unit: 'px' }
                    },
                    {
                        display: 'Padding Top',
                        fullWidth: true,
                        group,
                        max: '100',
                        name: 'paddingTop',
                        options: TypeConfig.options.measurement(),
                        type: 'measurement-slider'
                    },
                    {
                        display: 'Padding Bottom',
                        fullWidth: true,
                        group,
                        max: '100',
                        name: 'paddingBottom',
                        options: TypeConfig.options.measurement(),
                        type: 'measurement-slider'
                    }
                ]
            }
        }
    }
}
const TransactionalInventoryListBlockTypeDefinition = (group) => {
    return {
        id: Name.Block.Inventory.List,
        config: {
            block: {},
            layout: TransactionalInventoryListTypeConfig.panel.layout(),
            defaults: [
                { configName: 'block', propertyName: 'cell', propertyValue: InventoryCellContainer.TypeConfig.defaultDesignTransactional() }
            ]
        },
        display: 'Inventory Container',
        group,
        image: 'inventory.png'
    };
}
const Transactional = { BlockTypeDefinition: TransactionalInventoryListBlockTypeDefinition, Component: TransactionalInventoryListComponent, TypeConfig: TransactionalInventoryListTypeConfig };

function InventoryRow(props) {
    return (
        <table
            border="0"
            cellPadding="0"
            cellSpacing="0"
            className="InventoryBlock"
            width="100%">
            <tbody className="InventoryBlockOuter">
                <tr>
                    <td style={props.style} valign="top">
                        {
                            props.renderMso &&
                            <Comment document={props.frameDoc} id={`${props.blockId}-row${props.index}-top`} value={`<!--[if (gte mso 9)|(IE)]><table align="center" border="0" cellspacing="0" cellpadding="0" width="600" style="display:inline-table;width:600px;"><tr><![endif]-->`} />
                        }
                        {props.children}
                        {
                            props.renderMso &&
                            <Comment document={props.frameDoc} id={`${props.blockId}-row${props.index}-bottom`} value={`<!--[if (gte mso 9)|(IE)]></tr></table><![endif]-->`} />
                        }
                    </td>
                </tr>
            </tbody>
        </table>
    );
}

export { Campaign, Transactional };